// Die Schritte des Projektteasers (FfpvTeaserBoard.jsx): Deal waehlen,
// Angaben mit Bild, Vorschau, Erzeugen. Nur Anzeige - Zustand und Aufrufe
// liegen im Board.
//
// Stil wie das Abwaegen (designTokens, abwaegenAnzeige): Abschnittsmarken
// "2 · ANGABEN", ruhige Rahmen, genau ein oranger Knopf je Schritt.

import React, { useEffect, useRef, useState } from 'react';
import {
  AlertCircle, Camera, CheckCircle2, Download, ImageOff, Loader2, RefreshCw, Send, Upload, X,
} from 'lucide-react';
import { T, feldStil, karteStil, knopfStil, markeStil, primaerKnopfStil } from '../../lib/designTokens';
import { Ueberzeile, textKnopfStil } from '../contracts/abwaegenAnzeige';
import {
  A4_BREITE_PX, A4_HOEHE_PX, BILD_TYPEN, bytesText, dealKennung, fehlendText, flaecheText, herkunft,
  vorschauMassstab,
} from '../../lib/ffpvTeaser';

const TON = {
  gut: { schrift: T.gut, rand: T.gutRand, grund: T.gutGrund },
  info: { schrift: T.info, rand: T.infoRand, grund: T.infoGrund },
  warn: { schrift: T.warn, rand: T.warnRand, grund: T.warnGrund },
  gefahr: { schrift: T.gefahr, rand: T.gefahrRand, grund: T.gefahrGrund },
  neutral: { schrift: T.muted, rand: T.rand, grund: T.grund },
};

export function Marke({ ton = 'neutral', children, titel = '' }) {
  const f = TON[ton] || TON.neutral;
  return <span title={titel} style={markeStil(f.schrift, f.rand, f.grund)}>{children}</span>;
}

export function Fehlerkasten({ children }) {
  if (!children) return null;
  return (
    <div role="alert" style={{
      display: 'flex', gap: 8, alignItems: 'flex-start', padding: '9px 12px', borderRadius: T.rChip,
      background: T.gefahrGrund, borderLeft: `3px solid ${T.gefahr}`, color: T.gefahr,
      fontSize: T.tMeta, fontWeight: 600, lineHeight: 1.5,
    }}>
      <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
      <span>{children}</span>
    </div>
  );
}

export function Laedt({ children }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: T.muted, fontSize: T.tText }}>
      <Loader2 size={14} className="animate-spin" /> {children}
    </div>
  );
}

function gesperrt(aus) {
  return { opacity: aus ? 0.45 : 1, cursor: aus ? 'not-allowed' : 'pointer' };
}

// ---------------------------------------------------------------- Schritt 1

/** Die Warteschlange der Ausloeserphase als Liste zum Anklicken. */
export function DealWahl({ items, laedt, fehler, onWahl }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <Ueberzeile nummer={1} stil={{ flex: 1 }}>Deal wählen</Ueberzeile>
        {items.length ? <span style={{ fontSize: T.tMikro, color: T.muted }}>{items.length} warten</span> : null}
      </div>
      <div style={{ fontSize: T.tMeta, color: T.muted, lineHeight: 1.5 }}>
        Deals in der Phase „Teaser benötigt“ – oder oben eine Deal-ID eintragen.
      </div>

      <Fehlerkasten>{fehler}</Fehlerkasten>

      {laedt && items.length === 0 ? <Laedt>Warteschlange wird aus Bitrix geladen …</Laedt> : null}

      {!laedt && !fehler && items.length === 0 ? (
        <div style={{ padding: '14px 12px', borderRadius: T.rChip, background: T.grund, color: T.muted, fontSize: T.tText, lineHeight: 1.55 }}>
          Gerade wartet kein Deal auf einen Teaser. Die Deal-ID oben eintragen – das geht auch ohne die Phase.
        </div>
      ) : null}

      {items.length > 0 ? (
        <div style={{ border: `1px solid ${T.randWeich}`, borderRadius: T.rKarte, overflow: 'hidden' }}>
          {items.map((item, i) => {
            const fehlt = fehlendText(item.fehlend);
            const unterzeile = [dealKennung(item.dealId), item.region, flaecheText(item.flaecheHa)].filter(Boolean).join(' · ');
            return (
              <button key={item.dealId} type="button" onClick={() => onWahl(item)} style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                border: 'none', borderTop: i === 0 ? 'none' : `1px solid ${T.hairline}`, background: T.flaeche,
                textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: T.tText, fontWeight: 700, color: T.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.title || `Deal ${item.dealId}`}
                  </div>
                  <div style={{ fontSize: T.tMikro, color: T.muted, marginTop: 2 }}>{unterzeile}</div>
                </div>
                {item.hatBild === false ? <Marke ton="warn">ohne Bild</Marke> : null}
                {fehlt ? <Marke ton="neutral" titel={fehlt}>{item.fehlend.length} fehlen</Marke> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- Schritt 2

/** Bild der Flaeche: das aus Bitrix, oder hochgeladen (Datei oder Kamera). */
export function BildWahl({ quelle, herkunftQuelle, hinweis = '', bild, laedt, fehler, gesperrt: aus, onDatei, onEntfernen }) {
  const dateiFeld = useRef(null);
  const kameraFeld = useRef(null);
  const anzeige = bild?.dataUrl || quelle;
  const marke = bild
    ? { ton: 'info', text: 'hochgeladen' }
    : (quelle ? { ton: 'gut', text: herkunftQuelle === 'mapping' ? 'Mapping' : 'Bitrix' } : { ton: 'warn', text: 'fehlt' });

  const gewaehlt = (ereignis) => {
    const datei = ereignis.target.files?.[0];
    // Zuruecksetzen, damit dieselbe Datei nach einem Fehler erneut waehlbar ist.
    ereignis.target.value = '';
    if (datei) onDatei(datei);
  };

  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <input ref={dateiFeld} type="file" accept={BILD_TYPEN.join(',')} onChange={gewaehlt} style={{ display: 'none' }} />
      <input ref={kameraFeld} type="file" accept="image/*" capture="environment" onChange={gewaehlt} style={{ display: 'none' }} />

      <div style={{
        width: 168, height: 112, flexShrink: 0, borderRadius: T.rChip, overflow: 'hidden',
        border: `1px solid ${T.randWeich}`, background: T.grund,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {anzeige ? (
          <img src={anzeige} alt="Bild der Fläche" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{ textAlign: 'center', color: T.faint, fontSize: T.tMikro, padding: 8, lineHeight: 1.4 }}>
            <ImageOff size={18} style={{ display: 'block', margin: '0 auto 4px' }} />
            Im PDF steht „Luftbild folgt“
          </div>
        )}
      </div>

      <div style={{ flex: '1 1 200px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 7 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: T.tSm, fontWeight: 600, color: T.body }}>Bild der Fläche</span>
          <Marke ton={marke.ton}>{marke.text}</Marke>
        </div>
        {bild ? (
          <div style={{ fontSize: T.tMikro, color: T.muted, lineHeight: 1.45, wordBreak: 'break-all' }}>
            {[bild.name, `${bild.breite}×${bild.hoehe}`, bytesText(bild.bytes)].filter(Boolean).join(' · ')}
            {bild.verkleinert ? ' · verkleinert' : ''}
          </div>
        ) : null}
        {hinweis ? <div style={{ fontSize: T.tMikro, color: T.warn, lineHeight: 1.45 }}>{hinweis}</div> : null}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" disabled={aus || laedt} onClick={() => dateiFeld.current?.click()}
            style={{ ...knopfStil(), ...gesperrt(aus || laedt) }}>
            <Upload size={12} /> {bild || quelle ? 'Anderes Bild' : 'Bild hochladen'}
          </button>
          <button type="button" disabled={aus || laedt} onClick={() => kameraFeld.current?.click()}
            title="Mit der Kamera aufnehmen (am Laptop öffnet sich die Dateiauswahl)"
            style={{ ...knopfStil(), ...gesperrt(aus || laedt) }}>
            <Camera size={12} /> Foto
          </button>
          {bild ? (
            <button type="button" disabled={aus || laedt} onClick={onEntfernen} style={textKnopfStil}>
              Hochgeladenes verwerfen
            </button>
          ) : null}
        </div>
        {laedt ? <Laedt>Bild wird vorbereitet …</Laedt> : null}
        <Fehlerkasten>{fehler}</Fehlerkasten>
        <div style={{ fontSize: T.tMikro, color: T.faint }}>
          Wird auf höchstens 1600 px und 1,2 MB verkleinert und nur für diesen Teaser verwendet, nicht gespeichert.
        </div>
      </div>
    </div>
  );
}

function FeldZeile({ zeile, offen, gesperrt: aus, onOeffnen, onNachtrag, onZuruecksetzen, onEnter }) {
  const h = zeile.nachgetragen
    ? herkunft('nachtrag')
    : herkunft(zeile.quelle, { optional: zeile.optional, platzhalter: zeile.platzhalter });
  const zeigeFeld = offen || zeile.nachgetragen;
  const leerText = zeile.optional ? 'leer' : 'fehlt – bitte nachtragen';

  // Eine Zeile je Angabe: Beschriftung | Wert | Herkunft. Zweizeilig passten
  // kaum sechs Angaben neben die Vorschau.
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'minmax(96px, 30%) minmax(0, 1fr) auto', columnGap: 10,
      alignItems: 'center', padding: '7px 0', borderTop: `1px solid ${T.hairline}`, minHeight: 34,
    }}>
      <span title={zeile.beschreibung} style={{ fontSize: T.tSm, fontWeight: 600, color: T.body, cursor: 'help' }}>
        {zeile.beschriftung}
      </span>
      {zeigeFeld ? (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', minWidth: 0 }}>
          <input
            value={zeile.nachtrag}
            onChange={(e) => onNachtrag(zeile.platzhalter, e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') onEnter(); }}
            placeholder={zeile.wert && !zeile.nachgetragen ? `Bisher: ${zeile.wert}` : 'Wert nur für diesen Teaser'}
            aria-label={`${zeile.beschriftung} nachtragen`}
            disabled={aus}
            autoFocus={offen && !zeile.nachgetragen}
            style={{ ...feldStil, padding: '7px 9px' }}
          />
          <button type="button" onClick={() => onZuruecksetzen(zeile.platzhalter)} disabled={aus}
            title="Nachtrag verwerfen – dann gilt wieder der Wert aus Bitrix bzw. dem Mapping"
            aria-label={`Nachtrag für ${zeile.beschriftung} verwerfen`}
            style={{ ...knopfStil(), padding: '7px 8px', flexShrink: 0 }}>
            <X size={12} />
          </button>
        </div>
      ) : (
        <span style={{
          minWidth: 0, fontSize: T.tText, lineHeight: 1.45, wordBreak: 'break-word',
          color: zeile.leer ? (zeile.optional ? T.faint : T.warn) : T.ink, fontWeight: zeile.leer ? 600 : 400,
        }}>
          {zeile.leer ? leerText : zeile.wert}
        </span>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
        <Marke ton={h.ton}>{h.text}</Marke>
        {!zeigeFeld ? (
          <button type="button" onClick={() => onOeffnen(zeile.platzhalter)} disabled={aus}
            aria-label={`${zeile.beschriftung} ${zeile.leer ? 'nachtragen' : 'ändern'}`}
            style={{ ...textKnopfStil, minWidth: 62, justifyContent: 'flex-end', color: zeile.leer && !zeile.optional ? T.orangeTinte : T.muted }}>
            {zeile.leer ? 'nachtragen' : 'ändern'}
          </button>
        ) : <span style={{ width: 62 }} />}
      </div>
    </div>
  );
}

/** Alle Angaben mit Herkunft, gruppiert; Nachtraege gelten nur fuer diesen Teaser. */
export function AngabenListe({
  aufbereitet, offen, gesperrt: aus, veraltet, laedt, fehler, bildBaustein,
  onOeffnen, onNachtrag, onZuruecksetzen, onAktualisieren,
}) {
  const { zeilen, fehlendPflicht, entfallen } = aufbereitet;
  const gruppen = zeilen.reduce((liste, zeile) => {
    const letzte = liste[liste.length - 1];
    return letzte && letzte.name === zeile.gruppe
      ? [...liste.slice(0, -1), { ...letzte, zeilen: [...letzte.zeilen, zeile] }]
      : [...liste, { name: zeile.gruppe, zeilen: [zeile] }];
  }, []);
  const zaehler = [
    fehlendPflicht ? `${fehlendPflicht} fehlen` : '',
    entfallen ? `${entfallen} entfallen` : '',
  ].filter(Boolean).join(' · ');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <Ueberzeile nummer={2} stil={{ flex: 1 }}>Angaben prüfen</Ueberzeile>
        {zaehler ? <span style={{ fontSize: T.tMikro, fontWeight: 600, color: fehlendPflicht ? T.warn : T.muted }}>{zaehler}</span> : null}
      </div>
      <div style={{ fontSize: T.tMeta, color: T.muted, lineHeight: 1.5 }}>
        Nachträge gelten nur für diesen Teaser und werden nie nach Bitrix geschrieben.
      </div>

      {/* Oben, nicht unter der langen Liste: dort sah man den Knopf erst
          nach dem Scrollen. Unten klebt zusaetzlich die Erzeugen-Leiste. */}
      {veraltet ? (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: T.rChip,
          background: T.warnGrund, border: `1px solid ${T.warnRand}`,
        }}>
          <span style={{ flex: 1, fontSize: T.tMeta, color: T.warn, fontWeight: 600 }}>
            Geändert – die Vorschau zeigt noch den alten Stand.
          </span>
          <button type="button" onClick={onAktualisieren} disabled={laedt} style={{ ...primaerKnopfStil, ...gesperrt(laedt) }}>
            {laedt ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Vorschau aktualisieren
          </button>
        </div>
      ) : null}

      {bildBaustein}

      <Fehlerkasten>{fehler}</Fehlerkasten>
      {laedt && zeilen.length === 0 ? <Laedt>Angaben werden aus Bitrix geholt …</Laedt> : null}

      {gruppen.map((gruppe) => (
        <div key={gruppe.name}>
          <Ueberzeile stil={{ margin: '6px 0 2px' }}>{gruppe.name}</Ueberzeile>
          {gruppe.zeilen.map((zeile) => (
            <FeldZeile key={zeile.platzhalter} zeile={zeile} offen={offen.has(zeile.platzhalter)} gesperrt={aus}
              onOeffnen={onOeffnen} onNachtrag={onNachtrag} onZuruecksetzen={onZuruecksetzen} onEnter={onAktualisieren} />
          ))}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- Schritt 3

/**
 * Das gefuellte A4-Dokument, auf die Spaltenbreite verkleinert. Das iframe
 * bleibt ohne Skripte (sandbox ohne allow-scripts); allow-same-origin nur,
 * damit hier die Hoehe des Inhalts messbar ist.
 */
export function TeaserVorschau({ html, laedt, veraltet, fehler, onAktualisieren }) {
  const huelle = useRef(null);
  const [breite, setBreite] = useState(0);
  const [seite, setSeite] = useState({ breite: A4_BREITE_PX, hoehe: A4_HOEHE_PX });

  useEffect(() => {
    const element = huelle.current;
    if (!element) return undefined;
    const messen = () => setBreite(element.clientWidth);
    messen();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const beobachter = new ResizeObserver(messen);
    beobachter.observe(element);
    return () => beobachter.disconnect();
  }, []);

  const geladen = (ereignis) => {
    const dokument = ereignis.currentTarget.contentDocument?.documentElement;
    if (!dokument) return;
    setSeite({
      breite: Math.max(A4_BREITE_PX, dokument.scrollWidth || 0),
      hoehe: Math.max(A4_HOEHE_PX, dokument.scrollHeight || 0),
    });
  };

  const massstab = vorschauMassstab(breite, seite.breite);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 24 }}>
        <Ueberzeile nummer={3} stil={{ flex: 1 }}>Vorschau</Ueberzeile>
        {laedt ? <Laedt>wird erstellt …</Laedt> : null}
        {!laedt && veraltet ? (
          <button type="button" onClick={onAktualisieren} style={{ ...textKnopfStil, color: T.orangeTinte }}>
            <RefreshCw size={11} /> veraltet – aktualisieren
          </button>
        ) : null}
      </div>
      <Fehlerkasten>{fehler}</Fehlerkasten>
      <div ref={huelle} style={{
        position: 'relative', width: '100%', height: html ? Math.ceil(seite.hoehe * massstab) : 260,
        overflow: 'hidden', borderRadius: T.rChip, background: T.grund, border: `1px solid ${T.randWeich}`,
      }}>
        {html ? (
          <iframe
            title="Vorschau Projektteaser"
            srcDoc={html}
            sandbox="allow-same-origin"
            onLoad={geladen}
            scrolling="no"
            style={{
              width: seite.breite, height: seite.hoehe, border: 'none', background: '#FFFFFF',
              transform: `scale(${massstab})`, transformOrigin: 'top left',
              opacity: laedt || veraltet ? 0.55 : 1, transition: 'opacity 150ms',
            }}
          />
        ) : (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.faint, fontSize: T.tMeta }}>
            {laedt ? 'Die Vorschau wird erstellt …' : 'Noch keine Vorschau.'}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Schritt 4

const ERGEBNIS_ICON = { gut: CheckCircle2, warn: AlertCircle, gefahr: AlertCircle };

/**
 * Die Leiste zum Erzeugen - klebt unten am Fenster, damit sie neben der
 * langen Vorschau immer erreichbar ist.
 */
export function ErzeugenLeiste({
  darfErzeugen, bereit, sperrGrund, fehlendPflicht, laeuft, phaseWechseln, onPhaseWechseln,
  onHerunterladen, onInDenDeal, ergebnis, fehler, pdf, onPdfSpeichern, veraltet, onAktualisieren,
}) {
  const aus = !bereit || Boolean(laeuft);
  const Icon = ergebnis ? ERGEBNIS_ICON[ergebnis.ton] || CheckCircle2 : null;
  const f = ergebnis ? TON[ergebnis.ton] || TON.gut : null;

  return (
    <div style={{
      ...karteStil, position: 'sticky', bottom: 0, zIndex: 5, padding: '11px 14px',
      boxShadow: `inset 3px 0 0 ${T.orange}, 0 -6px 18px rgba(18, 42, 92, 0.07)`,
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12,
    }}>
      <Ueberzeile nummer={4}>Erzeugen</Ueberzeile>

      {!darfErzeugen ? (
        <span style={{ fontSize: T.tMeta, color: T.muted }}>Zum Erzeugen fehlt dir die Berechtigung.</span>
      ) : (
        <>
          <span style={{ flex: '1 1 180px', minWidth: 0, fontSize: T.tMeta, fontWeight: 600, color: sperrGrund ? T.orangeTinte : T.warn }}>
            {sperrGrund || (fehlendPflicht ? `${fehlendPflicht} Angaben fehlen noch – dort bleibt das PDF leer.` : '')}
            {veraltet && !laeuft ? (
              <button type="button" onClick={onAktualisieren} style={{ ...textKnopfStil, color: T.orangeTinte, marginLeft: 8, textDecoration: 'underline' }}>
                Jetzt aktualisieren
              </button>
            ) : null}
          </span>
          <label title="Nur beim Ablegen im Deal" style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: T.tSm, color: T.body, fontWeight: 600, cursor: 'pointer',
          }}>
            <input type="checkbox" checked={phaseWechseln} onChange={(e) => onPhaseWechseln(e.target.checked)} disabled={Boolean(laeuft)} />
            Phase auf „Teaser fertig“ setzen
          </label>
          <button type="button" onClick={onInDenDeal} disabled={aus}
            title="PDF erzeugen und als Kommentar in die Timeline des Deals legen"
            style={{ ...knopfStil(), ...gesperrt(aus) }}>
            {laeuft === 'bitrix' ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} In den Deal legen
          </button>
          <button type="button" onClick={onHerunterladen} disabled={aus} style={{ ...primaerKnopfStil, ...gesperrt(aus) }}>
            {laeuft === 'pdf' ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} PDF herunterladen
          </button>
        </>
      )}

      {ergebnis || fehler ? (
        <div style={{ flexBasis: '100%', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Fehlerkasten>{fehler}</Fehlerkasten>
          {ergebnis ? (
            <div role="status" style={{
              display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 11px', borderRadius: T.rChip,
              background: f.grund, borderLeft: `3px solid ${f.schrift}`, color: f.schrift, fontSize: T.tMeta, fontWeight: 600,
            }}>
              <Icon size={14} style={{ flexShrink: 0, marginTop: 2 }} />
              <span style={{ flex: 1, lineHeight: 1.5 }}>{ergebnis.zeilen.join(' ')}</span>
              {pdf ? (
                <button type="button" onClick={onPdfSpeichern} style={{ ...textKnopfStil, color: f.schrift }}>
                  <Download size={11} /> {pdf.dateiname}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
